/**
 * AdminCostDashboard — platform-only spend, MRR, churn, per-workspace economics.
 *
 * Pulls from /api/admin/cost/* endpoints. Admin-only — server returns 403 for
 * non-admin users; UI redirects to /dashboard in that case.
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  DollarSign, Activity, TrendingDown, TrendingUp, Users, RefreshCw,
  Database, Mail, Cpu, Layers, AlertCircle,
} from "lucide-react";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";

const fmtUSD = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtInt = (n) => Number(n || 0).toLocaleString();

export default function AdminCostDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [openai, setOpenai] = useState(null);
  const [workspaces, setWorkspaces] = useState([]);
  const [resend, setResend] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  useEffect(() => {
    if (user && !user.is_admin) {
      toast.error("Admin only");
      navigate("/dashboard");
    }
  }, [user, navigate]);

  const load = async () => {
    setLoading(true);
    try {
      const [s, o, w, r] = await Promise.all([
        api.get("/admin/cost/summary"),
        api.get(`/admin/cost/openai?days=${days}`),
        api.get(`/admin/cost/workspaces?days=${days}&limit=50`),
        api.get(`/admin/cost/resend?days=${days}`),
      ]);
      setSummary(s.data);
      setOpenai(o.data);
      setWorkspaces(w.data || []);
      setResend(r.data);
    } catch (e) {
      toast.error(e.response?.status === 403 ? "Admin only" : "Failed to load cost data");
    }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days]);

  if (loading && !summary) return <div className="p-10 text-meta">Loading cost dashboard…</div>;
  if (!summary) return <div className="p-10 text-meta">Cost data unavailable.</div>;

  const margin = summary.profitability.gross_margin_pct;

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="admin-cost-dashboard">
      <div className="max-w-7xl mx-auto p-6 lg:p-10 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-wider text-meta-soft font-semibold mb-1">Admin · Internal only</p>
            <h1 className="font-display text-3xl font-bold text-ink">Cost & Profitability</h1>
            <p className="text-sm text-meta mt-1">Spend, margin, MRR, churn — never visible to customers.</p>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={days}
              onChange={(e) => setDays(parseInt(e.target.value, 10))}
              data-testid="cost-window-select"
              className="rounded-full border border-line bg-white px-3 py-1.5 text-xs text-ink-soft"
            >
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
            </select>
            <button
              type="button"
              onClick={load}
              data-testid="cost-refresh-btn"
              className="rounded-full border border-line bg-white px-3 py-1.5 text-xs text-meta hover:text-ink inline-flex items-center gap-1"
            >
              <RefreshCw className="h-3.5 w-3.5" /> Refresh
            </button>
          </div>
        </div>

        {/* KPI row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="kpi-row">
          <KPI label="MRR" value={fmtUSD(summary.subscriptions.mrr_usd)} sub={`ARR ${fmtUSD(summary.subscriptions.arr_usd)}`} icon={DollarSign} accent="emerald" data-testid="kpi-mrr" />
          <KPI label="Projected monthly burn" value={fmtUSD(summary.openai.projected_monthly_burn)} sub="OpenAI MTD extrapolated" icon={Cpu} accent="amber" data-testid="kpi-burn" />
          <KPI label="Gross margin" value={margin === null ? "—" : `${margin}%`} sub={margin === null ? "Need MRR" : margin > 70 ? "Healthy" : margin > 30 ? "Watch" : "At risk"} icon={margin > 50 ? TrendingUp : TrendingDown} accent={margin > 50 ? "emerald" : "rose"} data-testid="kpi-margin" />
          <KPI label="Active subs" value={fmtInt(summary.subscriptions.active)} sub={`${summary.subscriptions.paying} paying · ${summary.subscriptions.trialing} trial`} icon={Users} accent="violet" data-testid="kpi-subs" />
        </div>

        {/* OpenAI spend */}
        <section className="surface p-6" data-testid="openai-spend-card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-lg font-bold text-ink inline-flex items-center gap-2">
              <Cpu className="h-4 w-4 text-ai" /> OpenAI spend
            </h2>
            <p className="text-xs text-meta">Computed locally from token logs × static price table</p>
          </div>
          <div className="grid grid-cols-3 gap-4 mb-6">
            <Stat label="Today" value={fmtUSD(summary.openai.today.cost)} sub={`${fmtInt(summary.openai.today.tokens)} tokens`} />
            <Stat label="Month-to-date" value={fmtUSD(summary.openai.mtd.cost)} sub={`${fmtInt(summary.openai.mtd.calls)} calls`} />
            <Stat label="Last 30 days" value={fmtUSD(summary.openai.last_30d.cost)} sub={`${fmtInt(summary.openai.last_30d.tokens)} tokens`} />
          </div>

          {openai?.by_model?.length > 0 && (
            <div className="border-t border-line pt-4">
              <p className="text-xs font-semibold text-ink-soft uppercase tracking-wider mb-3">By model</p>
              <div className="space-y-2">
                {openai.by_model.map((row) => (
                  <Bar key={row.model} label={row.model} value={row.cost} max={openai.by_model[0].cost} sub={`${fmtInt(row.tokens)} tokens · ${fmtInt(row.calls)} calls`} />
                ))}
              </div>
            </div>
          )}

          {openai?.by_purpose?.length > 0 && (
            <div className="border-t border-line pt-4 mt-4">
              <p className="text-xs font-semibold text-ink-soft uppercase tracking-wider mb-3">By purpose</p>
              <div className="space-y-2">
                {openai.by_purpose.slice(0, 8).map((row) => (
                  <Bar key={row.purpose} label={row.purpose || "unknown"} value={row.cost} max={openai.by_purpose[0].cost} sub={`${fmtInt(row.calls)} calls`} />
                ))}
              </div>
            </div>
          )}
        </section>

        {/* Subscription health */}
        <section className="surface p-6" data-testid="subscription-health-card">
          <h2 className="font-display text-lg font-bold text-ink mb-4 inline-flex items-center gap-2">
            <Layers className="h-4 w-4 text-ai" /> Subscription health
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Stat label="Active" value={fmtInt(summary.subscriptions.active)} />
            <Stat label="Trialing" value={fmtInt(summary.subscriptions.trialing)} />
            <Stat label="Churned (30d)" value={fmtInt(summary.subscriptions.churned_30d)} />
            <Stat label="Churn rate (30d)" value={`${summary.subscriptions.churn_rate_pct_30d}%`} accent={summary.subscriptions.churn_rate_pct_30d > 5 ? "rose" : "emerald"} />
          </div>
        </section>

        {/* Per-workspace cost */}
        <section className="surface p-6" data-testid="workspace-cost-card">
          <h2 className="font-display text-lg font-bold text-ink mb-4 inline-flex items-center gap-2">
            <Database className="h-4 w-4 text-ai" /> Top workspaces by spend ({days}d)
          </h2>
          {workspaces.length === 0 ? (
            <p className="text-sm text-meta">No AI activity in window yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-line text-xs uppercase tracking-wider text-meta-soft text-left">
                    <th className="py-2 pr-3 font-semibold">Workspace</th>
                    <th className="py-2 pr-3 font-semibold">Plan</th>
                    <th className="py-2 pr-3 font-semibold text-right">AI cost</th>
                    <th className="py-2 pr-3 font-semibold text-right">Calls</th>
                    <th className="py-2 pr-3 font-semibold text-right">MRR</th>
                    <th className="py-2 pr-3 font-semibold text-right">Revenue / Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {workspaces.map((w) => {
                    const ratio = w.revenue_to_cost_ratio;
                    const ratioBad = ratio !== null && ratio < 3;
                    return (
                      <tr key={w.workspace_id} className="border-b border-line/60 last:border-0 hover:bg-slate-50" data-testid={`workspace-row-${w.workspace_id}`}>
                        <td className="py-2 pr-3 font-mono text-xs text-ink-soft">{w.workspace_id}</td>
                        <td className="py-2 pr-3"><span className={`pill ${w.plan === "ascend" ? "pill-ai" : ""}`}>{w.plan}</span></td>
                        <td className="py-2 pr-3 text-right font-semibold text-ink">{fmtUSD(w.openai_cost_usd)}</td>
                        <td className="py-2 pr-3 text-right text-meta">{fmtInt(w.ai_calls)}</td>
                        <td className="py-2 pr-3 text-right text-meta">{fmtUSD(w.mrr_contribution_usd)}</td>
                        <td className={`py-2 pr-3 text-right font-semibold ${ratioBad ? "text-rose-600" : "text-emerald-600"}`}>
                          {ratio === null ? "—" : `${ratio}×`}
                          {ratioBad && <AlertCircle className="inline h-3 w-3 ml-1" />}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Resend email volume */}
        {resend && (
          <section className="surface p-6" data-testid="resend-card">
            <h2 className="font-display text-lg font-bold text-ink mb-4 inline-flex items-center gap-2">
              <Mail className="h-4 w-4 text-ai" /> Email volume ({days}d)
            </h2>
            <p className="text-sm text-meta mb-3">Total sent: <strong className="text-ink">{fmtInt(resend.total_sent)}</strong></p>
            {resend.by_purpose?.length > 0 && (
              <div className="space-y-2">
                {resend.by_purpose.map((row) => (
                  <Bar key={row.purpose} label={row.purpose} value={row.sent} max={resend.by_purpose[0].sent} sub={`${fmtInt(row.delivered)} delivered`} />
                ))}
              </div>
            )}
          </section>
        )}
      </div>
    </div>
  );
}

function KPI({ label, value, sub, icon: Icon, accent }) {
  return (
    <div className="surface p-4">
      <div className="flex items-center gap-2 mb-2">
        <div className={`rounded-xl p-2 bg-${accent}-50 text-${accent}-600`}>
          <Icon className="h-4 w-4" />
        </div>
        <p className="text-xs uppercase tracking-wider text-meta-soft font-semibold">{label}</p>
      </div>
      <p className="font-display text-2xl font-bold text-ink">{value}</p>
      {sub && <p className="text-xs text-meta mt-1">{sub}</p>}
    </div>
  );
}

function Stat({ label, value, sub, accent }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wider text-meta-soft font-semibold mb-1">{label}</p>
      <p className={`font-display text-xl font-bold ${accent ? `text-${accent}-600` : "text-ink"}`}>{value}</p>
      {sub && <p className="text-xs text-meta mt-0.5">{sub}</p>}
    </div>
  );
}

function Bar({ label, value, max, sub }) {
  const pct = max > 0 ? (value / max * 100) : 0;
  return (
    <div>
      <div className="flex items-center justify-between text-xs text-ink-soft mb-1">
        <span className="font-medium">{label}</span>
        <span>{typeof value === "number" && value < 100 ? `$${value.toFixed(2)}` : value}</span>
      </div>
      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
        <div className="h-full bg-violet-500" style={{ width: `${pct}%` }} />
      </div>
      {sub && <p className="text-[10px] text-meta-soft mt-1">{sub}</p>}
    </div>
  );
}
